/* eslint-disable ember/no-runloop */
import { get, set } from '@ember/object';
import { begin, end } from '@ember/runloop';
import { type Registry as Services, service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import { isEmpty } from '@ember/utils';
import Component from '@glimmer/component';
import { PrismTooltipHeader } from '@mylighthouse/prism-ember';
import {
  PRISM_SPACING_100,
  PRISM_SPACING_300,
} from '@mylighthouse/prism-foundation';
import dayjs from 'dayjs';
import getChartColor from 'frontend-utils/utils/hotel-chart-color';
import GeneralChartsOtaChart from 'lumen/components/general/charts/ota-chart';
import HotelRankingTooltipsTooltipRankingCalendar from 'lumen/components/hotel/ranking/tooltips/tooltip-ranking-calendar';

interface HotelRankingChartsChartDistributionSignature {
  Args: {
    onUpdateChartReference: unknown;
    type: unknown;
  };
}

export default class HotelRankingChartsChartDistribution extends Component<HotelRankingChartsChartDistributionSignature> {
  // eslint-disable-next-line ember/no-unused-services
  @service declare dates: Services['dates'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  // eslint-disable-next-line ember/no-unused-services
  @service declare intl: Services['intl'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get chartData() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const selectedOTA = this.args.dataForSelectedPeriod[0]?.ota?.id;
    let hotels = [this.hotel?.hotelinfo];

    if (this.hotelAndMarketService.activeCompset?.competitors) {
      hotels = [
        ...hotels,
        ...(this.hotelAndMarketService.activeCompset?.competitors ?? []),
      ];
    }

    const data = hotels
      .map((hotel) => {
        let hasData = false;
        // @ts-expect-error: Incorrect type
        const isCurrentHotel = this.hotel?.hotelinfo?.id === hotel.id;

        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
        const current = this.args.dataForSelectedPeriod
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          .filter((rank) => rank.hotelinfo?.id === hotel.id)
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          .map((hotelRanking) => {
            if (hotelRanking) {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
              const date = hotelRanking.extractDatetime;
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              const y =
                // @ts-expect-error: Incorrect type
                get(hotelRanking, this.args.type) === 0
                  ? null
                  : // @ts-expect-error: Incorrect type
                    get(hotelRanking, this.args.type);

              hasData = y !== null;

              return {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
                x: date.unix() * 1000,
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                y,
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                hotelinfo: hotelRanking.hotelinfo,
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                message: hotelRanking.message,
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                tooltipValue: y,
              };
            }
            return null;
          })
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          .filter((item) => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            if (isEmpty(item) || item[1] === 0) {
              return false; // filter out missing and score = 0
            }
            return true;
          });

        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        if (hasData || hotel.otas.map((ota) => ota.id).includes(selectedOTA)) {
          return {
            // @ts-expect-error: Incorrect type
            name: hotel.name,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            data: current,
            color: getChartColor(
              // @ts-expect-error: Incorrect type
              hotel.id,
              this.hotelAndMarketService.primaryAndSecondaryCompetitors,
            ),
            connectNulls: true,
            lineWidth: isCurrentHotel ? 5 : 2,
            marker: {
              radius: isCurrentHotel ? 5 : 4,
            },
          };
        }
        return null;
      })
      .filter((serie) => serie !== null);
    return data;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get chartOptions() {
    const minTickInterval =
      // @ts-expect-error: Incorrect type
      this.args.period === 'weekly'
        ? 3600 * 24 * 7 * 1000
        : 3600 * 24 * 30 * 1000;
    const optionsDict = {
      chart: {
        type: 'line',
        spacingRight: 0,
        spacingBottom: 0,
        spacingLeft: PRISM_SPACING_100, // For some reason highcharts draws over the border so setting it to a slight padding aligns better with the real edge
      },
      plotOptions: {
        series: {
          animation: false,
          stickyTracking: false,
        },
      },
      title: {
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        text: `ranking-${this.args.type}-chart`,
      },
      xAxis: {
        type: 'datetime',
        minTickInterval, // time in milliseconds
        minRange: minTickInterval,
        labels: {
          rotation: -45,
        },
      },
      yAxis: {
        title: {
          text: '',
        },
        plotLines: [
          {
            value: 0,
            width: 1,
            color: 'var(--prism-color-border-neutral-default)',
          },
        ],
      },
      tooltip: this.tooltip,
      legend: {
        enabled: true,
        // layout: 'horizontal',
        // align: 'center',
        // verticalAlign: 'bottom',
        borderWidth: 0,
        layout: 'vertical',
        align: 'right',
        verticalAlign: 'top',
        y: 20,
        x: -10,
        padding: PRISM_SPACING_300,
        itemStyle: {
          color: 'var(--prism-color-text-neutral-subdued)',
          width: 200,
          textOverflow: 'ellipsis',
          overflow: 'hidden',
        },
        itemHoverStyle: {
          color: 'var(--prism-color-interactive-text-secondary-hover)',
        },
      },
    };
    if (this.args.type === 'ranking') {
      // @ts-expect-error: Incorrect type
      optionsDict.yAxis.min = 1;
      // @ts-expect-error: Incorrect type
      optionsDict.yAxis.reversed = true;
    }
    return optionsDict;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hotel() {
    return this.hotelAndMarketService.hotel;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get tooltip() {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;
    return {
      shared: false,
      hideDelay: 0.2,
      useHTML: true,
      padding: 0,
      shadow: false,
      borderWidth: 0,
      /*
        The formatter will set the necessary value needed for the tooltip component.
        The highcharts formatter requires html to be returned (in this case)
        so we need to get the rendered html from the template
        The tooltip component is placed in a dummy div where this html can be obtained.
        Caution: the data for the component needs to be set in a different run loop,
        otherwise stale data will be returned
      */
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      formatter() {
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const { chart } = this.series;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const { series } = chart;
        let index = 0;
        const sortFactor = that.args?.type !== 'ranking' ? -1 : 1;
        while (
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          series[0] &&
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          series[0].data[index] &&
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          this.x !== series[0].data[index].x
        ) {
          index += 1;
        } // compute the index of corr y value in each data arrays
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
        const items = series
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          .map(({ color, data }) => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            if (data[index]) {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              const y = data[index].tooltipValue
                ? // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                  Math.round(data[index].y * 10) / 10
                : 0;

              return {
                ranking: y,
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                hotel: data[index].hotelinfo,
                backgroundColor: htmlSafe(`background-color:${color};`),
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                message: data[index].message,
              };
            }
            return null;
          })
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          .sort((a, b) => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            if (a.ranking === 0) {
              return sortFactor;
            }
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            if (b.ranking === 0) {
              return -1 * sortFactor;
            }
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            return a.ranking - b.ranking;
          });
        if (that.args?.type !== 'ranking') {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          items.reverse();
        }
        begin();
        set(that, 'tooltipData', items);
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
        const pointMoment = dayjs.utc(this.point.x);
        const formattedPoint = pointMoment.format(
          that.dates.partialDateFormat.YEAR_AND_MONTH_LONG,
        );
        const tooltipTitle =
          // @ts-expect-error: Incorrect type
          that.args.period === 'weekly'
            ? `${formattedPoint} (${that.intl.t('general.week')} ${pointMoment.week()})`
            : formattedPoint;
        // http://api.highcharts.com/highcharts/tooltip
        set(that, 'month', tooltipTitle);
        end();
        // @ts-expect-error: Incorrect type
        return document.getElementById(
          // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
          `dummy-div-for-tooltip-distribution-${that.args.type}`,
        ).innerHTML;
      },
    };
  }

  <template>
    <GeneralChartsOtaChart
      {{! @glint-expect-error: Incorrect type }}
      @chartOptions={{this.chartOptions}}
      @model={{this.chartData}}
      {{! @glint-expect-error: Incorrect type }}
      @onUpdateChartReference={{@onUpdateChartReference}}
    />
    <div class="hidden" id="dummy-div-for-tooltip-distribution-{{@type}}">
      <div class="arrival-day-tooltip max-w-full">
        <PrismTooltipHeader>
          {{! @glint-expect-error: Incorrect type }}
          {{this.month}}
        </PrismTooltipHeader>
        <HotelRankingTooltipsTooltipRankingCalendar
          {{! @glint-expect-error: Incorrect type }}
          @month={{this.month}}
          {{! @glint-expect-error: Incorrect type }}
          @tooltipData={{this.tooltipData}}
          {{! @glint-expect-error: Incorrect type }}
          @type={{this.type}}
        />
      </div>
    </div>
  </template>
}
